import { Add, Edit, ManageAccounts, Person } from '@mui/icons-material';
import { Grid, Typography, List, ListItem, ListItemIcon, ListItemText, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { ContentSection } from 'src/shared/components/organisms/contentSection';
import { useGlobals } from 'src/shared/contexts/appContext';
import { useState } from 'react';
import { getPermission } from 'src/shared/functions/getSysProductPermissions';
import { PermissionEnum } from 'src/shared/models/enums/permissionEnums';
import { Member } from '../models/member';
import { MemberFamilyEditModal } from './memberFamilyEditModal';
import { MemberFamilyCreateCouplingModal } from './memberFamilyCreateCouplingModal';

type MemberFamilyMembersProps = {
  member: Member;
  showEditButtons?: boolean | undefined;
  refetchMemberCallback: () => void;
};

export const MemberFamilyMembers = (props: MemberFamilyMembersProps) => {
  const { member, showEditButtons = true, refetchMemberCallback } = props;
  const { t } = useTranslation(['members']);
  const { currentUser, currentMember } = useGlobals();

  const [openFamilyEditModal, setOpenFamilyEditModal] = useState(false);
  const [openFamilyCreateModal, setOpenFamilyCreateModal] = useState(false);

  const hasMemberUpdatePermission = getPermission(currentUser, PermissionEnum.MemberUpdate);

  const onEditButtonHandler = () => {
    setOpenFamilyEditModal(true);
  };

  const onCreateButtonHandler = () => {
    setOpenFamilyCreateModal(true);
  };

  const hideFamilyMembers = () => {
    const isMember = currentMember;
    const noFamilyMembers = member?.familyMembers && member.familyMembers.length === 0;
    return isMember && noFamilyMembers;
  };

  // Dont show this sections if the user has no family members and the user is of type member
  if (hideFamilyMembers()) return null;

  return (
    <Grid item xs={12}>
      <ContentSection
        header={t('member:memberDetails.houseHoldMember')}
        showHeaderCounter={!!(member?.familyMembers && member.familyMembers.length > 0)}
        headerCounter={member.familyMembers?.length}
        button={
          // Ett vänmedlemskap har inget eget hushåll. Hushållet hör till det ordinarie
          // medlemskapet, och knappen döljs därför på vänposten.
          <>
            {currentUser && showEditButtons && !member.isExtraMember && (
              <>
                {member.familyMembers && member.familyMembers.length > 0 ? (
                  <Button
                    disabled={!hasMemberUpdatePermission}
                    variant='outlined'
                    size='small'
                    onClick={onEditButtonHandler}
                    endIcon={<Edit fontSize='small' />}
                  >
                    {t('common:button.edit')}
                  </Button>
                ) : (
                  <Button
                    disabled={!hasMemberUpdatePermission}
                    variant='outlined'
                    size='small'
                    onClick={onCreateButtonHandler}
                    endIcon={<Add fontSize='small' />}
                  >
                    {t('common:button.create')}
                  </Button>
                )}
              </>
            )}
          </>
        }
      >
        <List dense>
          {member.familyMembers?.map((item) => (
            <ListItem sx={{ py: 0, pl: 0.5 }} key={item.member_id}>
              <ListItemIcon sx={{ width: '2rem', maxWidth: '2rem', minWidth: '2rem' }}>
                {item.mainMember && <ManageAccounts fontSize='small' />}
                {!item.mainMember && <Person fontSize='small' />}
              </ListItemIcon>
              <ListItemText
                primary={
                  <>
                    {currentUser ? (
                      <Link to={`${RoutesEnum.MemberDetails}/${item.member_id}`}>{`${item.firstName} ${item.lastName}`}</Link>
                    ) : (
                      <Typography component='span'>{`${item.firstName} ${item.lastName}`}</Typography>
                    )}
                    {item.mainMember && (
                      <Typography component='span' variant='caption' sx={{ fontWeight: 500, ml: 0.5 }}>
                        ({t('member:memberDetails.headMember')})
                      </Typography>
                    )}
                  </>
                }
              />
            </ListItem>
          ))}
        </List>
      </ContentSection>
      {openFamilyEditModal && member && (
        <MemberFamilyEditModal
          modalOpen={openFamilyEditModal}
          member={member}
          onClose={() => {
            setOpenFamilyEditModal(false);
            refetchMemberCallback();
          }}
          refetchMemberCallback={() => {
            refetchMemberCallback();
          }}
        />
      )}
      {openFamilyCreateModal && member && (
        <MemberFamilyCreateCouplingModal
          modalOpen={openFamilyCreateModal}
          onClose={() => setOpenFamilyCreateModal(false)}
          refetchMemberCallback={refetchMemberCallback}
          memberAssociationId={member.association?.localAss_id ?? 0}
          memberId={member.member_id ?? 0}
          mainMemberLocalAssId={member.localAss_id ?? 0}
        />
      )}
    </Grid>
  );
};
